// src/pages/TicketStockOverview/modals/StockOverviewDetailModal.tsx
// Popup chi tiết giao vé của 1 kỳ: danh sách phiếu giao vé (tái dùng GetPaginatedTicketDeliveriesQuery
// đã có sẵn PlanId+AgentId filter), phân trang, filter theo đại lý bằng autocomplete — copy nguyên
// pattern debounce từ CreateTicketDeliveryModal.tsx (REQUIRE_FIXBUGS.md mục 8/10).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Label } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getAgents, getTicketDeliveriesApi } from 'helpers/fakebackend_helper';
import { IAgent } from 'slices/agentManagement/types';
import { AgentMap, buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import {
  TICKET_DELIVERY_STATUS_COLORS,
  TICKET_DELIVERY_STATUS_LABELS,
  TicketDelivery,
} from 'pages/TicketDeliveryManagement/types';
import { StockOverviewPagination } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_stock_overview');

interface AgentOption {
  value: number;
  label: string;
}

interface StockOverviewDetailModalProps {
  isOpen: boolean;
  planId: number | null;
  planCode?: string;
  onClose: () => void;
}

const PAGE_SIZE = 10;

const initialPagination: StockOverviewPagination = {
  currentPage: 1,
  pageSize: PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
};

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const columnCount = 7;

const StockOverviewDetailModal: React.FC<StockOverviewDetailModalProps> = ({
  isOpen,
  planId,
  planCode,
  onClose,
}) => {
  const [deliveries, setDeliveries] = useState<TicketDelivery[]>([]);
  const [pagination, setPagination] = useState<StockOverviewPagination>(initialPagination);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agentSearchInput, setAgentSearchInput] = useState('');
  const [defaultAgentOptions, setDefaultAgentOptions] = useState<AgentOption[]>([]);
  const [agentMap, setAgentMap] = useState<AgentMap>({});
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const agentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Map agentId -> mã/tên để hiển thị cột "Đại lý" (DTO chỉ trả agentId, xem helpers/agentLookup.ts)
  useEffect(() => {
    if (!isOpen) return;
    loadAgentsWithCache().then((agents) => setAgentMap(buildAgentMap(agents)));
  }, [isOpen]);

  // Reset bộ lọc + trang mỗi lần mở popup cho 1 kỳ khác
  useEffect(() => {
    if (isOpen) {
      setSelectedAgent(null);
      setAgentSearchInput('');
      setPagination(initialPagination);
    }
  }, [isOpen, planId]);

  const mapAgentOption = (agent: IAgent): AgentOption => ({
    value: Number(agent.id),
    label: `${agent.code} - ${agent.name}`,
  });

  // Autocomplete đại lý — debounce 350ms, giống hệt CreateTicketDeliveryModal.tsx
  const loadAgentOptions = useCallback(
    (inputValue: string): Promise<AgentOption[]> =>
      new Promise((resolve) => {
        if (agentSearchTimer.current) clearTimeout(agentSearchTimer.current);
        agentSearchTimer.current = setTimeout(async () => {
          try {
            const response = await getAgents({
              PageIndex: 1,
              PageSize: 20,
              Status: 'Active',
              Search: inputValue || undefined,
            });
            resolve((getPayloadItems(response) as IAgent[]).map(mapAgentOption));
          } catch (error: any) {
            toast.error(error?.message || st('khong_the_tim_dai_ly_message'));
            resolve([]);
          }
        }, 350);
      }),
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getAgents({ PageIndex: 1, PageSize: 20, Status: 'Active' })
      .then((response) => {
        if (!cancelled) setDefaultAgentOptions((getPayloadItems(response) as IAgent[]).map(mapAgentOption));
      })
      .catch(() => {
        if (!cancelled) setDefaultAgentOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Nạp danh sách phiếu giao của kỳ đang xem — theo PlanId + AgentId (nếu có chọn lọc) + trang
  useEffect(() => {
    if (!isOpen || !planId) return;
    let cancelled = false;
    setLoading(true);
    // Chỉ đưa key `agentId` vào params khi thật sự đã chọn lọc theo đại lý — nếu truyền
    // `agentId: undefined`, APIClient.get (helpers/api_helper.ts) nối thẳng value vào query string
    // mà không lọc undefined/null, ép thành chuỗi "undefined" và bị BE trả 400 (mục 13.1).
    const deliveryParams: Record<string, any> = {
      planId,
      pageIndex: pagination.currentPage,
      pageSize: pagination.pageSize,
    };
    if (selectedAgent?.value) deliveryParams.agentId = selectedAgent.value;

    getTicketDeliveriesApi(deliveryParams)
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data || response;
        setDeliveries(payload?.items || payload?.data || []);
        const totalItems = payload?.totalItems ?? payload?.totalCount ?? 0;
        setPagination((prev) => ({
          ...prev,
          totalItems,
          totalPages: payload?.totalPages ?? Math.ceil(totalItems / Math.max(prev.pageSize, 1)),
        }));
      })
      .catch((error: any) => {
        if (!cancelled) toast.error(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, planId, selectedAgent, pagination.currentPage, pagination.pageSize]);

  const toggleExpanded = (deliveryId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);
      return next;
    });
  };

  const renderStatusBadge = (delivery: TicketDelivery) => {
    const color =
      delivery.statusBadgeColor || TICKET_DELIVERY_STATUS_COLORS[String(delivery.status)] || 'secondary';
    const label =
      delivery.statusName || TICKET_DELIVERY_STATUS_LABELS[String(delivery.status)] || st('khong_ro_text');
    return <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>{label}</span>;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>
        {st('chi_tiet_giao_ve_title')} {planCode || (planId ? `#${planId}` : '')}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3" style={{ maxWidth: 320 }}>
          <Label for="stock-overview-agent-filter">{st('loc_theo_dai_ly_label')}</Label>
          <AsyncSelect
            inputId="stock-overview-agent-filter"
            isClearable
            cacheOptions
            defaultOptions={defaultAgentOptions}
            loadOptions={loadAgentOptions}
            value={selectedAgent}
            inputValue={agentSearchInput}
            onInputChange={(value, actionMeta) => {
              if (actionMeta.action === 'input-change') setAgentSearchInput(value);
              return value;
            }}
            onChange={(option) => {
              setSelectedAgent(option as AgentOption | null);
              setAgentSearchInput('');
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            placeholder={st('go_ma_hoac_ten_dai_ly_placeholder')}
            noOptionsMessage={() => st('khong_tim_thay_dai_ly_phu_text')}
            loadingMessage={() => st('dang_tim_text')}
          />
        </div>

        <DataTableShell
          headerRow={
            <tr>
              <th style={{ width: 40 }}></th>
              <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
              <th>{st('ma_phieu_column')}</th>
              <th>{st('dai_ly_column')}</th>
              <th className="text-end">{st('so_luong_ve_column')}</th>
              <th>{st('trang_thai_column')}</th>
              <th>{st('ngay_giao_column')}</th>
            </tr>
          }
        >
          {loading ? (
            Array.from({ length: 5 }).map((_, row) => (
              <tr key={row}>
                {Array.from({ length: columnCount }).map((__, col) => (
                  <td key={col}>
                    <div className="placeholder-glow">
                      <span className="placeholder col-8"></span>
                    </div>
                  </td>
                ))}
              </tr>
            ))
          ) : deliveries.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="text-center text-muted py-5">
                {st('chua_co_phieu_giao_ve_nao_text')}{selectedAgent ? st('cho_dai_ly_nay_text') : ''}
              </td>
            </tr>
          ) : (
            deliveries.map((delivery, index) => {
              const lines = delivery.lines || [];
              const isExpanded = expandedIds.has(delivery.id);
              const canExpand = lines.length > 0;
              return (
                <React.Fragment key={delivery.id}>
                  {/* Dòng cha = 1 đại lý (1 phiếu giao) — mục 13.1 */}
                  <tr
                    role={canExpand ? 'button' : undefined}
                    onClick={canExpand ? () => toggleExpanded(delivery.id) : undefined}
                    style={canExpand ? { cursor: 'pointer' } : undefined}
                  >
                    <td className="text-center">
                      {canExpand && (
                        <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-18 text-muted`} />
                      )}
                    </td>
                    <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                    <td>
                      <div className="fw-medium text-primary">{delivery.deliveryCode}</div>
                    </td>
                    <td>
                      <div className="fw-medium">{getAgentName(agentMap, delivery.agentId)}</div>
                      <small className="text-muted">{getAgentCode(agentMap, delivery.agentId)}</small>
                    </td>
                    <td className="text-end">{formatNumber(delivery.totalQuantity)}</td>
                    <td>{renderStatusBadge(delivery)}</td>
                    <td>{formatDate(delivery.deliveredAt || delivery.createdAt)}</td>
                  </tr>
                  {/* Dòng con — mỗi dòng 1 ký hiệu trong phiếu, hiển thị khi mở rộng */}
                  {isExpanded &&
                    lines.map((line, lineIndex) => (
                      <tr key={`${delivery.id}-${lineIndex}`} className="table-light">
                        <td></td>
                        <td></td>
                        <td colSpan={2} className="text-muted ps-4">
                          {st('ky_hieu_text')} <span className="fw-medium">{line.symbol}</span> {st('dai_text')}{' '}
                          {line.rangeStart}–{line.rangeEnd}
                        </td>
                        <td className="text-end text-muted">{formatNumber(line.quantity)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })
          )}
        </DataTableShell>

        <AgentPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
        />
      </ModalBody>
    </Modal>
  );
};

export default StockOverviewDetailModal;
